import { useEffect, useState } from "react";
import { CreditCard, Wallet, Minus, Plus, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { api, brl, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const ProviderOption = ({ id, label, icon: Icon, active, available, onSelect, note }) => (
  <button
    type="button"
    data-testid={`select-payment-${id}`}
    disabled={!available}
    onClick={() => onSelect(id)}
    className={`flex flex-1 items-center gap-3 rounded-xl border p-4 text-left transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-40 ${
      active ? "border-cyan-400 bg-cyan-400/10" : "border-white/10 hover:border-white/30"
    }`}
  >
    <Icon className={`h-5 w-5 ${active ? "text-cyan-300" : "text-slate-400"}`} />
    <div>
      <p className="font-semibold">{label}</p>
      <p className="font-mono-tech text-[10px] text-slate-500">{available ? note : "Indisponível no momento"}</p>
    </div>
  </button>
);

const PackageList = ({ packages, value, onChange }) => (
  <div className="grid gap-3">
    {packages.map((p) => (
      <button
        key={p.id}
        type="button"
        data-testid={`package-option-${p.id}`}
        onClick={() => onChange(p.id)}
        className={`flex items-center justify-between rounded-xl border p-4 transition-colors duration-200 ${value === p.id ? "border-lime-400 bg-lime-400/10" : "border-white/10 hover:border-white/30"}`}
      >
        <span className="flex items-center gap-3">
          <span className={`flex h-5 w-5 items-center justify-center rounded-full border ${value === p.id ? "border-lime-400 bg-lime-400 text-black" : "border-white/30"}`}>
            {value === p.id && <Check className="h-3 w-3" />}
          </span>
          <span className="text-left">
            <b className="block font-display text-lg uppercase">{p.name}</b>
            <span className="text-xs text-slate-400">{p.photos} fotos · {brl(p.price / p.photos)} por foto</span>
          </span>
        </span>
        <span className="font-display text-2xl font-bold">{brl(p.price)}</span>
      </button>
    ))}
  </div>
);

export const CheckoutDialog = ({ open, onOpenChange, contest, defaultKind = "photo" }) => {
  const { settings } = useAuth();
  const [kind, setKind] = useState(defaultKind);
  const [qty, setQty] = useState(1);
  const [pack, setPack] = useState(null);
  const [provider, setProvider] = useState("stripe");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setKind(contest ? "contest" : defaultKind);
      setPack(settings?.packages?.[1]?.id || settings?.packages?.[0]?.id || null);
      setProvider(settings?.stripe_available ? "stripe" : "mercadopago");
    }
  }, [open, contest, defaultKind, settings]);

  if (!settings) return null;
  const selPack = settings.packages.find((p) => p.id === pack);
  const total = kind === "photo" ? settings.extra_photo_price * qty : kind === "package" ? selPack?.price || 0 : contest?.entry_fee || 0;

  const pay = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        provider, kind, quantity: qty, package_id: pack, contest_id: contest?.id, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="checkout-dialog" className="max-h-[92vh] max-w-lg overflow-y-auto border-white/10 bg-[#0E0E15]">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-black uppercase">Checkout</DialogTitle>
          <DialogDescription>Fotos extras liberam sua participação nos concursos principais.</DialogDescription>
        </DialogHeader>
        <Tabs value={kind} onValueChange={setKind}>
          <TabsList className="w-full bg-white/5">
            <TabsTrigger value="photo" data-testid="checkout-tab-photo" className="flex-1">Foto avulsa</TabsTrigger>
            <TabsTrigger value="package" data-testid="checkout-tab-package" className="flex-1">Pacotes</TabsTrigger>
            {contest && <TabsTrigger value="contest" data-testid="checkout-tab-contest" className="flex-1">Inscrição</TabsTrigger>}
          </TabsList>
          <TabsContent value="photo" className="mt-4">
            <div className="flex items-center justify-between rounded-xl border border-white/10 p-4">
              <div>
                <p className="font-display text-lg uppercase">Fotos extras</p>
                <p className="text-xs text-slate-400">{brl(settings.extra_photo_price)} por foto</p>
              </div>
              <div className="flex items-center gap-3">
                <button data-testid="checkout-qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="rounded-full border border-white/20 p-1.5 hover:border-cyan-400"><Minus className="h-4 w-4" /></button>
                <span data-testid="checkout-qty-value" className="w-6 text-center font-display text-2xl">{qty}</span>
                <button data-testid="checkout-qty-plus" onClick={() => setQty((q) => Math.min(50, q + 1))} className="rounded-full border border-white/20 p-1.5 hover:border-cyan-400"><Plus className="h-4 w-4" /></button>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="package" className="mt-4">
            <PackageList packages={settings.packages} value={pack} onChange={setPack} />
          </TabsContent>
          {contest && (
            <TabsContent value="contest" className="mt-4">
              <div className="rounded-xl border border-lime-400/30 bg-lime-400/5 p-4">
                <p className="font-display text-lg uppercase">{contest.title}</p>
                <p className="text-xs text-slate-400">Taxa única de inscrição no concurso principal</p>
              </div>
            </TabsContent>
          )}
        </Tabs>
        <div className="mt-2 flex flex-col gap-3 sm:flex-row">
          <ProviderOption id="stripe" label="Cartão (Stripe)" icon={CreditCard} active={provider === "stripe"} available={settings.stripe_available} onSelect={setProvider} note="Crédito e débito" />
          <ProviderOption id="mercadopago" label="Mercado Pago" icon={Wallet} active={provider === "mercadopago"} available={settings.mercadopago_available} onSelect={setProvider} note="Pix, boleto e cartão" />
        </div>
        <button data-testid="checkout-pay-btn" disabled={loading || total <= 0} onClick={pay} className="btn-neon mt-2 flex w-full items-center justify-center gap-2 rounded-full py-3.5 font-semibold disabled:opacity-50">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Pagar <span data-testid="checkout-total">{brl(total)}</span>
        </button>
      </DialogContent>
    </Dialog>
  );
};
